<template>
  <div class="page-wrapper" v-if="!redirecting">
    <BrandHero :heading="brandConfig.landingHeading"/>

    <p class="body-paragraph content-col">{{ brandConfig.landingBody }}</p>

    <div class="sub-card content-col">
      <div class="sub-card__header">
        <span class="sub-card__header-label">SUBSCRIPTION INFORMATION</span>
      </div>
      <div class="sub-card__body">
        <div class="sub-card__price-row">
          <span class="sub-card__price">{{ brandConfig.price }}</span>
          <span class="sub-card__price-suffix">{{ brandConfig.priceSuffix }}</span>
        </div>
        <div class="sub-card__rows">
          <div class="sub-card__row">
            <span class="sub-card__label">Service:</span>
            <span class="sub-card__value">Battle League Gaming</span>
          </div>
          <div class="sub-card__row">
            <span class="sub-card__label">Billing:</span>
            <span class="sub-card__value">Recurring weekly subscription charges of <b>RM5.00</b> to your U Mobile account</span>
          </div>
          <div class="sub-card__row">
            <span class="sub-card__label">Renewal:</span>
            <span class="sub-card__value">Automatically renews&nbsp; every week until cancelled</span>
          </div>
          <div class="sub-card__row">
            <span class="sub-card__label">Cancellation:</span>
            <span class="sub-card__value">Cancel anytime within the BLG portal</span>
          </div>
        </div>
      </div>
    </div>

    <button
        class="accept-btn content-col"
        :style="{ backgroundColor: brandConfig.buttonColor, color: brandConfig.buttonTextColor }"
        :disabled="submitting"
        @click="onSubscribeClicked">
      {{ brandConfig.landingCtaLabel }}
    </button>

    <TermsFooter class="terms-footer-spacing"/>
  </div>
</template>

<script>
import {mapState} from "vuex";
import BrandHero from "@/components/BrandHero.vue";
import TermsFooter from "@/components/TermsFooter.vue";
import getCampaignParametersFromUrl from "@/functions/getCampaignParametersFromUrl";
import {buildTrxId} from "@/functions/buildTrxId";
import {buildSubscribeFields, submitUmobileSubscribe} from "@/functions/submitUmobileSubscribe";
import {triggerGTMEvent} from "@/functions/addGTM";
import {getFlowMode} from "@/functions/getFlowMode";

export default {
  name: 'LandingPage',
  components: {BrandHero, TermsFooter},
  data() {
    return {
      submitting: false,
      redirecting: getFlowMode().autoRedirect
    }
  },
  computed: {
    ...mapState(['brandConfig'])
  },
  mounted() {
    if (this.redirecting) this.onTransparentFlow();
  },
  methods: {
    onSubscribeClicked() {
      if (this.submitting) return;
      this.submitting = true;

      try {
        triggerGTMEvent('subscribe_button_clicked');
        this.submitSubscribe();
      } catch (error) {
        this.submitting = false;
        this.$router.push({name: 'Error'});
      }
    },
    onTransparentFlow() {
      try {
        triggerGTMEvent('transparent_redirect');
        this.submitSubscribe();
      } catch (error) {
        this.redirecting = false;
      }
    },
    submitSubscribe() {
      const campaignParameters = getCampaignParametersFromUrl();
      const requestId = buildTrxId(campaignParameters);
      const fields = buildSubscribeFields(this.brandConfig, requestId);
      submitUmobileSubscribe(fields);
    }
  }
}
</script>

<style scoped>
.page-wrapper {
  min-height: 100vh;
  padding-bottom: 29.3px;
}

.content-col {
  width: min(330px, 100% - 44px);
  margin-left: auto;
  margin-right: auto;
}

.body-paragraph {
  margin-top: 26.5px;
  margin-bottom: 0;
  font-family: 'Montserrat', sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 17px;
  text-align: center;
  color: #FFFFFF;
}

.sub-card {
  margin-top: 33.2px;
  border: 1px solid rgba(179, 245, 13, 0.54);
  border-radius: 5px;
  background-color: transparent;
  overflow: hidden;
}

/* Band is 46 tall from the card's outer edge, so the 1px top border eats 1 of it.
   The band's own bottom rule is a single device pixel at 3x — 0.333 CSS px, a
   third of the card border. A full 1px at this alpha reads 3x too heavy. */
.sub-card__header {
  height: 45px;
  padding: 0 19px;
  background-color: rgba(179, 245, 13, 0.05);
  border-bottom: 0.333px solid rgba(179, 245, 13, 0.646);
  display: flex;
  align-items: flex-start;
}

.sub-card__header-label {
  margin-top: 14.4px;
  font-family: 'Clash Display', sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 1;
  color: #B3F50D;
}

.sub-card__body {
  padding: 0 19px;
}

.sub-card__price-row {
  margin-top: 14.3px;
}

.sub-card__price {
  font-family: 'Clash Display', sans-serif;
  font-weight: 700;
  font-size: 18px;
  line-height: 1;
  color: #FFFFFF;
}

.sub-card__price-suffix {
  margin-left: 7px;
  font-family: 'Montserrat', sans-serif;
  font-weight: 600;
  font-size: 10px;
  color: #B7B7B7;
}

/* flex gives the hanging indent on the wrapped Billing / Renewal lines for free —
   the second line starts at the value column's left edge, as measured. */
.sub-card__row {
  display: flex;
  align-items: baseline;
  gap: 6.5px;
  line-height: 12px;
  font-family: 'Montserrat', sans-serif;
  font-size: 10px;
}

.sub-card__row + .sub-card__row {
  margin-top: 9px;
}

.sub-card__row:first-child {
  margin-top: 12.7px;
}

.sub-card__label {
  flex: 0 0 auto;
  font-weight: 600;
  color: #FFFFFF;
}

.sub-card__value {
  flex: 1 1 auto;
  font-weight: 400;
  color: #B3B3B3;
}

.sub-card__value b {
  font-weight: 600;
  color: inherit;
}

.sub-card__rows {
  padding-bottom: 28.8px;
}

.accept-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 38px;
  height: 66px;
  border: none;
  border-radius: 4px;
  font-family: 'Clash Display', sans-serif;
  font-weight: 600;
  font-size: 15px;
  line-height: 1;
  cursor: pointer;
  transition: background-color 0.3s, transform 0.2s;
}

.accept-btn:hover:not(:disabled) {
  transform: translateY(-2px);
}

.accept-btn:disabled {
  cursor: not-allowed;
  transform: none;
  opacity: 0.7;
}

.terms-footer-spacing {
  margin-top: 20.7px;
}
</style>
